import React, { JSX, useEffect, useMemo, useState } from "react";

import { DashboardReportsApi } from "api/DashboardReportsApi";
import { useDeleteReportGeneration, useGetGeneratedReportData } from "api/api-hooks/reports-hooks";
import { GeneratedDashboardReportResponse } from "api/model/dashboard/GeneratedDashboardReportResponse";
import { ReportStatus, ReportType } from "api/model/enums";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Button } from "primereact/button";
import { GeneratedReportTableProps } from "components/ComponentTypeTypes";
import {
  Column,
  ColumnFilterApplyClickEvent,
  ColumnFilterElementTemplateOptions,
  ColumnFilterMetaDataWithConstraint,
} from "primereact/column";
import { ConfirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { RadioButton } from "primereact/radiobutton";
import { Tooltip } from "react-tooltip";
import { T } from "services/lang/LanguageService";
import { TemplateLoadingType } from "utils/enums";
import { DATE_FORMATTER_MINUTES, DATE_PATTERN_SECONDS } from "utils/constants";
import { useCompanyTimezone, useDisplayTimezone } from "hooks/useTimezone";
import { addToDate, fmtDatetime, fmtDatetimeAdvanced } from "utils/time-utils";
import CalendarWrapper from "../../basic/calendar-wrapper/CalendarWrapper";
import { getTemplateLoadingRange } from "../product-template-table/utils/timeUtils";
import EmailReportForm from "./EmailReportForm";
import GenerateNewReport from "./GenerateNewReport";
import GeneratedReportHistoryList from "./GeneratedReportHistoryList";
import MarkForRegeneration from "./MarkForRegeneration";
import RemoveReportRange from "./RemoveReportRange";
import "./GeneratedReportTable.scss";
import { ReportDialogView } from "./GeneratedReportTableTypes";

type FilterType = {
  dashboardId?: Array<number>;
  onlyType?: ReportType;
};

const statusMap: Record<ReportStatus, JSX.Element> = {
  SUCCESS: (
    <i
      data-tooltip-content={T("alert.success")}
      style={{ color: "green" }}
      className="pi pi-check status-body-tooltip"
    />
  ),
  FAILED: (
    <i
      data-tooltip-content={T("alert.failed")}
      style={{ color: "red" }}
      className="pi pi-times status-body-tooltip"
    />
  ),
  FAILED_RETRYING: (
    <i
      data-tooltip-content={T("alert.failed-retrying")}
      style={{ color: "orange" }}
      className="pi pi-exclamation-triangle status-body-tooltip"
    />
  ),
  GENERATING: (
    <i data-tooltip-content={T("alert.generating")} className="pi pi-spin pi-spinner status-body-tooltip" />
  ),
  MARKED_FOR_REGENERATION: (
    <i
      data-tooltip-content={T("alert.marked-for-regeneration")}
      style={{ color: "red" }}
      className="pi pi-spin pi-hourglass status-body-tooltip"
    />
  ),
  QUEUED: (
    <i
      data-tooltip-content={T("alert.queued")}
      style={{ color: "orange" }}
      className="pi pi-spin pi-hourglass status-body-tooltip"
    />
  ),
};

const typeMap: Record<ReportType, JSX.Element> = {
  MANUAL: <i data-tooltip-content={T("word.report-table.manual")} className="pi pi-user type-body-tooltip" />,
  SCHEDULED: (
    <i data-tooltip-content={T("word.report-table.scheduled")} className="pi pi-server type-body-tooltip" />
  ),
};

const closedDialog: ReportDialogView = {
  remove: false,
  removeRange: false,
  generate: false,
  email: false,
  history: false,
};

export default function GeneratedReportTable(props: GeneratedReportTableProps) {
  const displayTz = useDisplayTimezone();
  const companyTz = useCompanyTimezone();

  const [from, setFrom] = useState<Date | undefined>(undefined);
  const [to, setTo] = useState<Date | undefined>(undefined);
  const [dateChanged, setDateChanged] = useState(false);
  const [filterInfo, setFilerInfo] = useState<FilterType>({});
  const [dialogView, setDialogView] = useState<ReportDialogView>(closedDialog);
  const [dialogFunction, setDialogFunction] = useState<"REMOVE" | "CLEANUP">("REMOVE");
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);

  const rowsPerPageOptions = props.itemsPerPage
    ? [props.itemsPerPage, 10, 20, 30]
        .sort((a, b) => a - b)
        .filter((x, i, a) => {
          return a.indexOf(x) === i;
        })
    : [10, 20, 30];

  const { data, isLoading, isError, refetch } = useGetGeneratedReportData(
    {
      id: props.ids,
      onlyType: props.type,
      from: from?.toISOString(),
      to: to?.toISOString(),
      sort: props.sort,
      status: props.status || [
        ReportStatus.GENERATING,
        ReportStatus.FAILED,
        ReportStatus.FAILED_RETRYING,
        ReportStatus.SUCCESS,
        ReportStatus.QUEUED,
        ReportStatus.MARKED_FOR_REGENERATION,
      ],
    },
    {
      enabled: !!from && !!to,
    }
  );

  const { mutate: deleteReportGeneration } = useDeleteReportGeneration({
    onSuccess: () => {
      easasAlert({
        summary: T("alert.report-table.report-generation-deleted"),
        severity: "success",
      });
    },
    onFailure: () => {
      easasAlert({
        summary: T("alert.report-table.failed-to-delete-report-generation"),
        severity: "error",
      });
    },
  });

  useEffect(() => {
    setDatesByConfig();
  }, []);

  const selectedReport = useMemo(() => {
    if (!selectedReportId) {
      return null;
    }
    return data?.find(report => report.id === selectedReportId);
  }, [selectedReportId, data]);

  const setDatesByConfig = () => {
    if (props.loadingConfig) {
      const { start, end } = getTemplateLoadingRange(
        {
          loadingType: TemplateLoadingType.RANGE,
          loadingConfig: props.loadingConfig,
        },
        props.instantReferenceTime,
        companyTz
      );

      setFrom(start);
      setTo(end);
    } else {
      setFrom(addToDate(new Date(), -1, "DAYS"));
      setTo(new Date());
    }
    setDateChanged(false);
  };

  const filterDashBoardId = (e: ColumnFilterApplyClickEvent) => {
    const event = e as unknown as Omit<ColumnFilterApplyClickEvent, "constraints"> & {
      constraints: ColumnFilterMetaDataWithConstraint;
    };

    const keyPart = event.constraints.constraints[0].value;

    if (keyPart) {
      const arr: GeneratedDashboardReportResponse[] = data ?? [];

      const ar = arr.reduce((acc, ob) => {
        if (ob.dashboardTitle.includes(keyPart) && ob.dashboardId) {
          acc.push(ob.dashboardId as number);
        }
        return acc;
      }, [] as number[]);

      const uniqueArr: Array<number> = [];
      for (let i = 0; i < ar.length; i++) {
        if (uniqueArr.indexOf(ar[i]) === -1) {
          uniqueArr.push(ar[i]);
        }
      }

      setFilerInfo(prev => ({ ...prev, dashboardId: uniqueArr }));
    } else {
      setFilerInfo(prev => ({ ...prev, dashboardId: undefined }));
    }
  };

  const onZipDownloadClick = () => {
    let dashboardIds = props.ids;
    let onlyType = props.type;

    if (filterInfo) {
      if (filterInfo.dashboardId) {
        if (filterInfo.dashboardId.length === 0) {
          console.warn("No ids was filtered");
        } else {
          dashboardIds = filterInfo.dashboardId;
        }
      }

      if (filterInfo.onlyType) {
        onlyType = filterInfo.onlyType;
      }
    }

    if (dashboardIds.length > 0 && from) {
      window.open(
        DashboardReportsApi.getDownloadZipUrl(dashboardIds, from.toISOString(), to?.toISOString(), onlyType)
      );
    }
  };

  const reportTypesCheckboxFiltering = (options: ColumnFilterElementTemplateOptions) => {
    return (
      <div>
        <div className="field-radiobutton">
          <RadioButton
            inputId="type"
            value={ReportType.SCHEDULED}
            name="types"
            onChange={e => {
              setFilerInfo(val => ({ ...val, onlyType: e.value }));
              options.filterCallback(e.value);
            }}
            checked={options.value === ReportType.SCHEDULED}
          />
          <label htmlFor="type">{T("word.report-table.scheduled")}</label>
        </div>
        <div className="field-radiobutton" style={{ marginBottom: 0 }}>
          <RadioButton
            inputId="type2"
            value={ReportType.MANUAL}
            name="types"
            onChange={e => {
              setFilerInfo(val => ({ ...val, onlyType: e.value }));
              options.filterCallback(e.value);
            }}
            checked={options.value === ReportType.MANUAL}
          />
          <label htmlFor="type2">{T("word.report-table.manual")}</label>
        </div>
      </div>
    );
  };

  const renderReportHistory = (rowData: GeneratedDashboardReportResponse) => (
    <div className="flex align-items-center">
      {rowData.fileIds && rowData.fileIds.length > 1 && (
        <Button
          icon="pi pi-list"
          size="small"
          className="p-button-text report-table-button"
          onClick={() => {
            setSelectedReportId(rowData.id);
            setDialogView({ ...closedDialog, history: true });
          }}
        />
      )}
    </div>
  );

  const downloadReportBody = (rowData: GeneratedDashboardReportResponse) => {
    return (
      <div className="flex align-items-center">
        {rowData.fileIds && rowData.fileIds.length > 0 && (
          <Button
            icon="pi pi-download"
            size="small"
            className="p-button-text report-table-button"
            onClick={() => {
              window.open(
                DashboardReportsApi.getDownloadUrl(rowData.id, rowData.fileIds[rowData.fileIds.length - 1])
              );
            }}
          />
        )}
      </div>
    );
  };

  const renderSendEmailButton = (rowData: GeneratedDashboardReportResponse) => {
    return (
      <div className="flex align-items-center">
        {rowData.fileIds && rowData.fileIds.length > 0 && (
          <Button
            icon="pi pi-envelope"
            size="small"
            className="p-button-text report-table-button"
            onClick={() => {
              setSelectedReportId(rowData.id);
              setDialogView({ ...closedDialog, email: true });
            }}
          />
        )}
      </div>
    );
  };
  const accept = () => {
    if (!selectedReportId) return;
    deleteReportGeneration({ genId: selectedReportId });
  };

  const deleteGenerationBody = (rowData: GeneratedDashboardReportResponse) => {
    return (
      <div className="flex align-items-center">
        <Button
          icon="pi pi-trash"
          style={{ color: "red" }}
          size="small"
          className="p-button-text report-table-button"
          onClick={() => {
            setDialogView({ ...closedDialog, remove: true });
            setSelectedReportId(rowData.id);
          }}
        />
      </div>
    );
  };

  const typeBody = (rowData: GeneratedDashboardReportResponse) => {
    return (
      <>
        <Tooltip anchorSelect=".type-body-tooltip" delayShow={200} />
        {typeMap[rowData.type]}
      </>
    );
  };

  const statusBody = (rowData: GeneratedDashboardReportResponse) => {
    return (
      <>
        <Tooltip anchorSelect=".status-body-tooltip" delayShow={200} />
        {statusMap[rowData.status]}
      </>
    );
  };

  if (isError) {
    return <div>{T("error.report-table.component-error")}</div>;
  }

  const handleCloseDialog = () => {
    setDialogView(closedDialog);
    setSelectedReportId(null);
  };

  return (
    <>
      <ConfirmDialog
        visible={dialogView.remove}
        onHide={handleCloseDialog}
        message={T("confirm-dialog.report-table.do-you-want-to-remove-this-report")}
        header={T("confirm-dialog.report-table.delete-confirmation")}
        icon="pi pi-info-circle"
        acceptClassName="p-button-danger"
        accept={accept}
      />
      <div className="flex flex-column gap-2 generated-report-table">
        {!props.hideHeader && (
          <div className="flex gap-2 align-items-end justify-content-between">
            <div className="flex gap-2 align-items-end">
              {props.showDateSelection && (
                <div className="flex gap-2">
                  <CalendarWrapper
                    value={from as Date}
                    onChange={e => {
                      setDateChanged(true);
                      setFrom(e);
                    }}
                  />
                  <CalendarWrapper
                    value={to as Date}
                    onChange={e => {
                      setDateChanged(true);
                      setTo(e);
                    }}
                  />
                </div>
              )}

              {dateChanged && (
                <div>
                  <Button
                    icon="pi pi-refresh"
                    className="component-editor-button icon-blue"
                    onClick={() => setDatesByConfig()}
                  />
                </div>
              )}
            </div>
            <div className="flex gap-1">
              <div>
                <Button
                  icon="pi pi-refresh"
                  className="component-editor-button icon-blue"
                  onClick={() => {
                    refetch().then(() =>
                      easasAlert({
                        summary: T("alert.report-table.data-reloaded-successfully"),
                        severity: "success",
                        life: 3000,
                      })
                    );
                  }}
                  disabled={!props.ids || props.ids.length === 0}
                />
              </div>
              <div>
                <Button
                  icon="pi pi-cog"
                  className="component-editor-button icon-orange"
                  label={T("action.generate")}
                  onClick={() => setDialogView({ ...closedDialog, generate: true })}
                  disabled={!props.ids || props.ids.length === 0}
                />
              </div>

              {props.superUserComponent && (
                <div>
                  <Button
                    icon="pi pi-history"
                    className="component-editor-button icon-orange"
                    label={T("action.cleanup")}
                    onClick={() => {
                      setDialogFunction("CLEANUP");
                      setDialogView({ ...closedDialog, removeRange: true });
                    }}
                    disabled={!props.ids || props.ids.length === 0}
                  />
                </div>
              )}

              {props.superUserComponent && (
                <div>
                  <Button
                    icon="pi pi-trash"
                    className="component-editor-button icon-red"
                    label={T("word.reports")}
                    onClick={() => {
                      setDialogFunction("REMOVE");
                      setDialogView({ ...closedDialog, removeRange: true });
                    }}
                    disabled={!props.ids || props.ids.length === 0}
                  />
                </div>
              )}
              <div>
                <Button
                  icon="pi pi-folder"
                  className="component-editor-button icon-blue"
                  label={T("action.download")}
                  onClick={onZipDownloadClick}
                  disabled={filterInfo.dashboardId?.length === 0 || props.ids.length === 0}
                />
              </div>
            </div>
          </div>
        )}
        <DataTable
          className="generated-report-table"
          size="small"
          value={data}
          paginator
          responsiveLayout="scroll"
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate={`${T("word.showing")} {first} - {last} / {totalRecords}`}
          rows={props.itemsPerPage || 10}
          rowsPerPageOptions={rowsPerPageOptions}
          loading={isLoading}
          sortMode="multiple"
        >
          <Column
            field="dashboardTitle"
            header={T("word.report")}
            sortable
            filter
            filterMatchMode="contains"
            filterField="dashboardTitle"
            onFilterApplyClick={filterDashBoardId}
            onFilterClear={() => setFilerInfo(e => ({ ...e, dashboardId: undefined }))}
            showFilterMatchModes={false}
            showFilterMenuOptions={false}
            filterPlaceholder="Search"
          />
          {!props.type && (
            <Column
              field="type"
              header={T("word.type")}
              style={{ width: "0" }}
              body={typeBody}
              sortable
              filter
              filterMatchMode="contains"
              filterElement={reportTypesCheckboxFiltering}
              onFilterClear={() => {
                setFilerInfo(e => ({ ...e, onlyType: undefined }));
              }}
              filterField="type"
              showFilterMatchModes={false}
              showFilterMenuOptions={false}
              filterPlaceholder="Search"
            />
          )}

          <Column field="status" header={T("word.status")} style={{ width: "0" }} body={statusBody} />

          <Column
            field="referenceTime"
            header={T("load-conf.reference-time")}
            body={e =>
              fmtDatetimeAdvanced(e.referenceTime, displayTz, {
                pattern: props.referenceTimePattern || DATE_FORMATTER_MINUTES,
                defaultToNow: true,
              })
            }
          />
          <Column
            field="scheduleTime"
            header={T("word.created")}
            body={e => fmtDatetime(e.generationTimeEnd, displayTz, DATE_PATTERN_SECONDS)}
          />
          <Column body={rowData => MarkForRegeneration({ rowData })} style={{ width: "0" }} />
          <Column body={renderReportHistory} style={{ width: "0" }} />
          <Column body={downloadReportBody} style={{ width: "0" }} />
          <Column body={renderSendEmailButton} style={{ width: "0" }} hidden={!props.showEmailButton} />
          <Column body={deleteGenerationBody} style={{ width: "0" }} />
        </DataTable>
      </div>

      <Dialog
        header={T("action.generate")}
        visible={dialogView.generate}
        style={{ minWidth: "15vw" }}
        onHide={handleCloseDialog}
      >
        <GenerateNewReport ids={props.ids} close={handleCloseDialog} loadingConfig={props.loadingConfig} />
      </Dialog>

      <Dialog
        header={T("word.report-table.report-history")}
        visible={dialogView.history}
        style={{ minWidth: "10vw" }}
        onHide={handleCloseDialog}
      >
        <GeneratedReportHistoryList
          data={selectedReport?.fileIds.map(e => ({ file: e })) ?? []}
          genId={selectedReportId!}
          closeDialog={handleCloseDialog}
        />
      </Dialog>

      <Dialog
        header={T("word.report-table.email-report")}
        visible={dialogView.email}
        style={{ width: "max(30rem,16vw)" }}
        onHide={handleCloseDialog}
      >
        <EmailReportForm
          recipients={props.emailRecipients ?? []}
          closeDialog={handleCloseDialog}
          genId={selectedReportId!}
          reportId={selectedReport?.fileIds[selectedReport.fileIds.length - 1]}
        />
      </Dialog>

      <Dialog
        header={
          dialogFunction === "REMOVE"
            ? T("label.report-table.remove-reports-range")
            : T("label.report-table.cleanup-reports-history")
        }
        visible={dialogView.removeRange}
        style={{ minWidth: "15vw" }}
        onHide={handleCloseDialog}
      >
        <RemoveReportRange
          ids={props.ids}
          close={handleCloseDialog}
          loadingConfig={props.loadingConfig}
          function={dialogFunction}
        />
      </Dialog>
    </>
  );
}
